import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import {
  EmissionsCategoryEnum,
  OperationReportTypeEnum,
} from '@shared/api/types';
import { itemsByIntensityChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { Card } from '@shared/components/ui/card';
import { cn, roundNumberToNDecimalPlaces } from '@shared/lib/utils';

function HighestEmissionsItemsCard({
  item,
  rating,
  totalPurchased,
  totalEmissions,
  pctOfEmissions,
}: {
  item: string;
  rating?: EmissionsCategoryEnum;
  totalPurchased: number;
  totalEmissions: number;
  pctOfEmissions: number;
}) {
  return (
    <Card className="grid grid-cols-11 items-center gap-4 border-teal-400 p-3">
      <Text
        includeHover
        className="col-span-3 !line-clamp-2 !text-ellipsis font-heading font-medium"
      >
        {item}
      </Text>
      <div className="col-span-2 mx-auto">
        <div
          className={cn('size-4 shrink-0 rounded-full mt-0.5')}
          style={{
            backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
          }}
        ></div>
      </div>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(totalPurchased, 2, false)}
      </Text>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(totalEmissions, 2, false)}
      </Text>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(pctOfEmissions, 2, false)}%
      </Text>
    </Card>
  );
}

interface PackagingItemsProps extends ReportSectionComponent {}

export default function PackagingItems({ reportType }: PackagingItemsProps) {
  const {
    result: { packaging },
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const data = {
    totalItems: packaging?.totalItems,
    totalRecords:
      reportType === OperationReportTypeEnum.purchase
        ? packaging?.totalPurchases
        : packaging?.totalSales,
    highest_emissions_items: packaging?.highestEmissionItems.map((item) => {
      return {
        emissionsTonnes: item.emissionsTonnes,
        name: item.name,
        rating: item.rating as EmissionsCategoryEnum,
        pctOfCompanyEmissions: item.pctOfTotalEmissions,
        weightKg: item.weightKg / 1000,
      };
    }),

    items_by_rating: packaging?.itemsByRating,
  };

  const chartData = data.items_by_rating?.map((item) => {
    const category = item.rating as keyof typeof PACKAGING_ITEMS_RATING;
    return {
      category,
      items: item.numberOfItems,
      fill: PACKAGING_ITEMS_RATING[category].color,
    };
  });

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          {reportType === OperationReportTypeEnum.purchase
            ? 'To start reducing your emissions, we recommend analysing the highest-emission items you purchased and looking to reduce the amount you purchase in favour of a less carbon-intensive alternative, where possible.'
            : 'To start reducing your emissions, we recommend analysing the highest-emission items you sell and looking to reduce their carbon intensity or promote less carbon-intensive alternatives, where possible.'}
        </Text>
        <div className="flex">
          <ReportMetric
            title="Total items"
            value={roundNumberToNDecimalPlaces(data.totalItems ?? 0, 0)}
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title={`Total ${reportTypeCopy.plural}`}
            value={roundNumberToNDecimalPlaces(data.totalRecords ?? 0, 0)}
            size="md"
            className="flex-1"
          />
        </div>
        <div className="space-y-2">
          <Text variant="h4">Items by carbon intensity</Text>
          <Text variant="reportBody">
            This chart shows the distribution of items {reportTypeCopy.past},
            broken down by their carbon rating.
          </Text>
          <div className="flex items-center">
            <SimplePieChart
              dataKey="items"
              nameKey="category"
              chartConfig={itemsByIntensityChartConfig}
              chartData={chartData!.reverse()}
              innerRadius={60}
              className="size-64"
            />
            <div className="flex h-full flex-1 flex-col items-start justify-start gap-y-3">
              {(
                ['Low', 'Medium', 'High'] as Array<
                  keyof typeof PACKAGING_ITEMS_RATING
                >
              ).map((rating, idx) => {
                return (
                  <div key={idx} className="flex w-full items-center gap-2">
                    <div className="flex gap-2">
                      <div
                        className={cn('size-4 shrink-0 rounded-sm mt-0.5 ')}
                        style={{
                          backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
                        }}
                      ></div>
                      <Text className="mt-0">{rating}</Text>
                    </div>

                    <div className="flex items-baseline">
                      <Text variant="unit" className="text-nowrap">
                        {PACKAGING_ITEMS_RATING[rating].threshold}
                      </Text>
                      <Unit variant="kgCO2e/kg" className="ml-1" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
      <>
        <Text variant="h3">Highest emissions items</Text>
        <div className="space-y-3">
          <div className="grid grid-cols-11 gap-x-4">
            <Text className="col-span-3">Packaging item</Text>
            <Text className="col-span-2 mx-auto">Rating</Text>
            <Text className="col-span-2 text-center">
              Total {reportTypeCopy.past} <Unit variant="kg" />
            </Text>
            <Text className="col-span-2 text-center">
              Total emissions <Unit variant="tCO2e" />
            </Text>
            <Text className="col-span-2 text-center">% of emissions</Text>
          </div>
          {data.highest_emissions_items?.map((item, idx) => {
            return (
              idx < 7 && (
                <HighestEmissionsItemsCard
                  key={idx}
                  item={item.name}
                  rating={item.rating}
                  totalPurchased={item.weightKg}
                  totalEmissions={item.emissionsTonnes}
                  pctOfEmissions={item.pctOfCompanyEmissions}
                />
              )
            );
          })}
        </div>
      </>
    </ReportContent>
  );
}
